import { useEffect, useState } from "react";

interface ContributionDay {
  date: string;
  contributionCount: number;
  color: string;
}

interface ContributionWeek {
  contributionDays: ContributionDay[];
}

interface ContributionCalendar {
  totalContributions: number;
  weeks: ContributionWeek[];
}

interface GitHubContributionData {
  personal: ContributionCalendar | null;
  enterprise: ContributionCalendar | null;
  merged: ContributionCalendar | null;
  debug?: {
    enterpriseConfigured: boolean;
    enterpriseError: { message: string; status: number | null } | null;
  };
  timestamp?: number;
  error?: {
    message: string;
  };
}

interface CachedGitHubData {
  data: GitHubContributionData;
  timestamp: number;
}

// Cache duration in milliseconds (24 hours)
const CACHE_DURATION = 24 * 60 * 60 * 1000;

/**
 * Custom hook for fetching and managing GitHub contribution data
 */
export function useGitHubContributions() {
  const [data, setData] = useState<GitHubContributionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Function to load static data generated at build time
  const loadStaticData = async () => {
    try {
      setLoading(true);

      // Fetch the static JSON file that was created during build
      const response = await fetch("/data/github-contributions.json");

      if (!response.ok) {
        throw new Error(`Error loading static data: ${response.status}`);
      }

      const staticData = await response.json();

      // Check if the data contains an error
      if (staticData.error) {
        console.warn("Static GitHub data contains an error:", staticData.error);
        setError(staticData.error.message);
      } else {
        setData(staticData);

        // Store in localStorage as a backup
        try {
          localStorage.setItem(
            "github-contributions",
            JSON.stringify({
              data: staticData,
              timestamp: Date.now(),
            }),
          );
        } catch (storageError) {
          console.warn("Failed to cache static GitHub data:", storageError);
        }

        setError(null);
      }
    } catch (err) {
      console.error("Error loading static GitHub data:", err);

      // Try to load from localStorage as fallback
      try {
        const cachedData = localStorage.getItem("github-contributions");
        if (cachedData) {
          const parsedCache: CachedGitHubData = JSON.parse(cachedData);
          if (parsedCache?.data) {
            setData(parsedCache.data);
            setError(null);
          }
        } else {
          setError("Unable to load GitHub contribution data");
        }
      } catch (storageError) {
        console.error("Failed to load cached GitHub data:", storageError);
        setError("Unable to load GitHub contribution data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh data by calling the API directly
  const refreshData = async () => {
    try {
      setRefreshing(true);
      setError(null);

      // Call the API to get fresh data
      const response = await fetch("/api/github");

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const newData = await response.json();
      setData(newData);

      // Cache the data with current timestamp
      try {
        const cacheObject: CachedGitHubData = {
          data: newData,
          timestamp: Date.now(),
        };

        localStorage.setItem(
          "github-contributions",
          JSON.stringify(cacheObject),
        );
      } catch (storageError) {
        console.warn("Failed to cache GitHub data:", storageError);
      }

      setError(null);
      return true;
    } catch (error) {
      console.error("Error refreshing GitHub data:", error);
      setError(`Failed to refresh: ${error.message || "Unknown error"}`);
      return false;
    } finally {
      setRefreshing(false);
    }
  };

  // Check if data is stale and needs refresh (older than 24 hours)
  const isDataStale = () => {
    if (!data || !data.timestamp) return false;
    return Date.now() - data.timestamp > CACHE_DURATION;
  };

  // Load data on component mount
  useEffect(() => {
    loadStaticData();
  }, []);

  return {
    data,
    loading,
    error,
    refreshing,
    refreshData,
    isDataStale,
  };
}

export type {
  GitHubContributionData,
  ContributionCalendar,
  ContributionWeek,
  ContributionDay,
};
